// Conciliación de pagos recurrentes de proveedor (línea principal / alquiler + costos asociados).
//
// Las ocurrencias mensuales de un recurrente son VIRTUALES (proveedoresRecurrentes.js las calcula
// en vivo desde una sola fila config, sin persistir una por mes). Esta capa persiste el HECHO
// "esta ocurrencia (proveedor, costo_asociado_id, año, mes) fue pagada por este movimiento
// bancario real", en la tabla `conciliacion_costo_recurrente` (hermana de pago_recurrente_fecha,
// misma identidad de ocurrencia con discriminador `costo_asociado_id` NULL = línea principal).
// Fuente única del hecho, con candado en base (10.190, dos índices únicos parciales por 10.202).
//
// Casa de uso: costos de proveedor que NUNCA facturan (ej. alquiler de oficina + GGCC). El pago
// crea un movimientos_caja `entidad='proveedor'` + `categoria_pago='pago_proveedor'` con el
// `cuenta_er` correcto (para que el gasto pegue en el ER, ya que no habrá factura que lo lleve),
// y la fila de conciliación saca la ocurrencia de Movimientos Futuros.
import { crearMovimiento } from "./flujoCaja";
import { confirmarMovimiento } from "./movimientosBancarios";
import { conceptoFlujoProveedor, getNombreProveedor } from "./proveedores";
import { client } from "./supabase";

// Corte del proyecto: no se concilian ocurrencias anteriores a 2026-01 (saldadas por saldo inicial).
const CORTE = "2026-01";

// Clave canónica de una ocurrencia (idéntica a la de pago_recurrente_fecha en proveedoresRecurrentes.js).
// `main` = línea principal (costo_asociado_id NULL); un id numérico = ese costo asociado.
export const claveOcurrenciaRecurrente = (proveedorId, año, mes, costoAsociadoId) =>
  `${proveedorId}-${año}-${mes}-${costoAsociadoId ?? "main"}`;

// Todas las conciliaciones del país (para el loader de la proyección). Opcionalmente acotado a
// una lista de proveedores.
export const getConciliacionesRecurrentes = async (pais, proveedorIds = null) => {
  let q = client
    .from("conciliacion_costo_recurrente")
    .select("id, proveedor_id, costo_asociado_id, periodo_año, periodo_mes, movimiento_bancario_id, movimiento_caja_id")
    .eq("pais", pais);
  if (proveedorIds?.length) q = q.in("proveedor_id", proveedorIds);
  const { data, error } = await q;
  if (error) throw error; // 10.177: no tragarse el error a [] — enmascara fallas.
  return data || [];
};

// Ocurrencias recurrentes PENDIENTES (no conciliadas) de un proveedor, dentro de [corte..mes que
// viene]. Línea principal (monto_recurrente) + costos asociados (mensuales y únicos). El monto va
// en su moneda de origen (UF/CLP/…) — es informativo para el modal; el monto real de la caja es el
// del movimiento bancario. Se permite hasta el mes siguiente (pago adelantado).
export const getOcurrenciasRecurrentesPendientes = async (pais, proveedorId) => {
  const { data: prov, error: eProv } = await client
    .from("proveedores")
    .select(
      "id, nombre, alias, concepto_flujo, monto_recurrente, moneda_recurrente, moneda_default, dia_pago, es_recurrente, recurrente_desde, recurrente_hasta, cuenta_er_default"
    )
    .eq("id", proveedorId)
    .maybeSingle();
  if (eProv) throw eProv;
  if (!prov) return { proveedor: null, ocurrencias: [] };

  const [{ data: costos, error: eCos }, concils] = await Promise.all([
    client
      .from("proveedor_costos_asociados")
      .select("id, proveedor_id, concepto, monto, moneda, dia_pago, tipo, fecha")
      .eq("proveedor_id", proveedorId),
    getConciliacionesRecurrentes(pais, [proveedorId]),
  ]);
  if (eCos) throw eCos;

  const conciliadas = new Set(
    concils.map((c) => claveOcurrenciaRecurrente(c.proveedor_id, c.periodo_año, c.periodo_mes, c.costo_asociado_id))
  );

  const ahora = new Date();
  // Techo = primer día del mes que viene → mesKey del próximo mes (permite adelantar un mes).
  const techoDate = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 1);
  const techo = `${techoDate.getFullYear()}-${String(techoDate.getMonth() + 1).padStart(2, "0")}`;

  const nombreFlujo = getNombreProveedor(prov);
  const ocurrencias = [];
  const pushMes = (año, mes, costoId, concepto, monto, moneda, diaPago) => {
    const mesKey = `${año}-${String(mes).padStart(2, "0")}`;
    if (mesKey < CORTE || mesKey > techo) return;
    if (conciliadas.has(claveOcurrenciaRecurrente(proveedorId, año, mes, costoId))) return;
    ocurrencias.push({
      costo_asociado_id: costoId, // null = línea principal
      concepto,
      periodo_año: año,
      periodo_mes: mes,
      monto,
      moneda,
      dia_pago: diaPago,
      mesKey,
    });
  };

  // Rango de meses [floor..min(hasta, techo)] para las series mensuales.
  const desde = prov.recurrente_desde ? prov.recurrente_desde.slice(0, 7) : CORTE;
  const floor = desde < CORTE ? CORTE : desde;
  const hasta = prov.recurrente_hasta ? prov.recurrente_hasta.slice(0, 7) : techo;
  const iterMeses = () => {
    const out = [];
    const [fy, fm] = floor.split("-").map(Number);
    const end = hasta < techo ? hasta : techo;
    let y = fy;
    let m = fm;
    let key = `${y}-${String(m).padStart(2, "0")}`;
    while (key <= end) {
      out.push([y, m]);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      key = `${y}-${String(m).padStart(2, "0")}`;
    }
    return out;
  };

  // Línea principal (alquiler): monto_recurrente en moneda_default.
  const montoMain = Number(prov.monto_recurrente || 0);
  if (prov.es_recurrente && montoMain > 0) {
    const conceptoMain = conceptoFlujoProveedor(nombreFlujo, prov.concepto_flujo);
    const monedaMain = prov.moneda_default || prov.moneda_recurrente || "CLP";
    for (const [y, m] of iterMeses()) pushMes(y, m, null, conceptoMain, montoMain, monedaMain, prov.dia_pago || 30);
  }

  // Costos asociados.
  for (const costo of costos || []) {
    const monto = Number(costo.monto || 0);
    if (monto <= 0) continue;
    const concepto = conceptoFlujoProveedor(nombreFlujo, costo.concepto || "Costo");
    const moneda = costo.moneda || "CLP";
    if (costo.tipo === "unico") {
      if (!costo.fecha) continue;
      const [y, m] = costo.fecha.slice(0, 7).split("-").map(Number);
      pushMes(y, m, costo.id, concepto, monto, moneda, costo.dia_pago || 30);
    } else {
      for (const [y, m] of iterMeses()) pushMes(y, m, costo.id, concepto, monto, moneda, costo.dia_pago || 30);
    }
  }

  ocurrencias.sort(
    (a, b) =>
      a.mesKey.localeCompare(b.mesKey) ||
      String(a.costo_asociado_id ?? "").localeCompare(String(b.costo_asociado_id ?? ""))
  );
  return { proveedor: prov, ocurrencias };
};

// Concilia N ocurrencias contra UN movimiento bancario (patrón N-por-bancario de edificios):
// crea una fila de movimientos_caja por ocurrencia (todas colgando del mismo movimiento_bancario_id),
// inserta las filas de conciliación, apunta movimientos_bancarios.movimiento_caja_id a la primera y
// procesa el bancario. Idempotente por movimiento_bancario_id.
//
// `asignaciones`: [{ costo_asociado_id, concepto, periodo_año, periodo_mes, monto }] — `monto` es el
// monto (en la moneda del banco) asignado a esa ocurrencia; la suma debe igualar el monto del banco.
export const conciliarOcurrenciasRecurrentes = async ({
  movimiento,
  proveedor,
  asignaciones,
  cuentaEr,
  pais,
  userId,
}) => {
  if (!asignaciones?.length) throw new Error("No hay ocurrencias seleccionadas");

  // Idempotencia por movimiento_bancario_id. Crea N filas → chequeo con limit(1), no maybeSingle().
  const { data: existentes, error: eSel } = await client
    .from("movimientos_caja")
    .select("id")
    .eq("movimiento_bancario_id", movimiento.id)
    .limit(1);
  if (eSel) throw eSel;
  if (existentes?.length) return { yaVinculado: true, cajaId: existentes[0].id };

  // 1) Una fila de caja por ocurrencia.
  const creadas = [];
  for (const a of asignaciones) {
    const monto = Number(a.monto);
    // eslint-disable-next-line no-await-in-loop
    const caja = await crearMovimiento({
      pais,
      tipo: "pago",
      fecha: movimiento.fecha,
      monto,
      monto_neto: monto,
      monto_impuesto: 0,
      moneda: movimiento.moneda,
      estado: "confirmado",
      descripcion: `${a.concepto} (${String(a.periodo_mes).padStart(2, "0")}/${a.periodo_año})`,
      entidad_tipo: "proveedor",
      entidad_id: proveedor.id,
      entidad_nombre: proveedor.nombre,
      categoria_pago: "pago_proveedor",
      cuenta_er: cuentaEr || null,
      origen: "clay",
      movimiento_bancario_id: movimiento.id,
      registrado_por: userId || null,
    });
    if (!caja?.id) throw new Error("No se pudo crear el movimiento en caja");
    creadas.push({ cajaId: caja.id, a });
  }
  const primeraCajaId = creadas[0].cajaId;

  // 2) Filas de conciliación (candado 10.190). Si falla (ej. carrera → ocurrencia ya conciliada),
  //    rollback de las cajas creadas para no dejar huérfanos (10.177: sin fallo silencioso).
  const filas = creadas.map(({ cajaId, a }) => ({
    pais,
    proveedor_id: proveedor.id,
    costo_asociado_id: a.costo_asociado_id ?? null,
    periodo_año: a.periodo_año,
    periodo_mes: a.periodo_mes,
    movimiento_bancario_id: movimiento.id,
    movimiento_caja_id: cajaId,
    created_by: userId || null,
  }));
  const { error: eConc } = await client.from("conciliacion_costo_recurrente").insert(filas);
  if (eConc) {
    await client
      .from("movimientos_caja")
      .delete()
      .in(
        "id",
        creadas.map((c) => c.cajaId)
      );
    throw eConc;
  }

  // 3) Banco → apuntar a la primera caja + procesar.
  await client.from("movimientos_bancarios").update({ movimiento_caja_id: primeraCajaId }).eq("id", movimiento.id);
  await confirmarMovimiento(movimiento.id, { movimiento_caja_id: primeraCajaId, procesado_por: userId || null });

  return { cajaId: primeraCajaId, n: creadas.length };
};
